import { useCallback } from "react";
import { useChatNotifications } from "../context/ChatNotificationsContext.jsx";
import { saveDocumentContentToDefaultVault } from "../services/saveDocumentToDefaultVault.js";
import { useChatApiClient } from "../context/ChatApiClientContext.jsx";

function getErrorMessage(err) {
  return (
    err?.response?.data?.msgCode?.[0]?.description ||
    err?.message ||
    "Failed to create document."
  );
}

/** Nombre único para el documento: "Document" + fecha y hora (evita duplicados). Exportado para usar en el top bar del canvas. */
export function getUniqueDocumentName(baseName = "Document") {
  const now = new Date();
  const date = now.toLocaleDateString("es-CL", { day: "2-digit", month: "2-digit", year: "numeric" });
  const time = now.toLocaleTimeString("es-CL", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  return baseName === "Document" ? `Document ${date} ${time}` : baseName;
}

/**
 * Hook para crear un documento en el backend a partir del contenido de un mensaje de chat.
 * Usa el primer vault de listDrives (My Workspace) como destino. Solo muestra toast en error.
 * Bajo acoplamiento: no conoce el panel ni los documentos en memoria.
 *
 * @returns {{ createDocumentFromMessage: (params: { title: string, content: string }) => Promise<void> }}
 */
export function useCreateDocumentFromMessage() {
  const apiClient = useChatApiClient();
  const toast = useChatNotifications();

  const createDocumentFromMessage = useCallback(
    async ({ title, content }) => {
      const docTitle = getUniqueDocumentName(title ?? "Document");
      const docContent = content ?? "";

      try {
        const result = await saveDocumentContentToDefaultVault(apiClient, {
          title: docTitle,
          content: docContent,
          metadataSource: 'chat_message',
        });
        if (!result.ok) {
          toast.error({ message: result.error });
          return;
        }
        return result.documentUuid;
      } catch (err) {
        toast.error({ message: getErrorMessage(err) });
      }
    },
    [toast, apiClient]
  );

  return { createDocumentFromMessage };
}
